import { useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Circle,
  Eye,
  EyeOff,
  Frame,
  Group,
  Image,
  Lock,
  Square,
  Type,
  Unlock,
} from "lucide-react";
import type { GameObject } from "./types";
import { childrenOf } from "./tree";
const ROW = 28;
const icons = {
  rect: Square,
  circle: Circle,
  image: Image,
  text: Type,
  group: Group,
  frame: Frame,
};
type Props = {
  objects: GameObject[];
  selected: string[];
  readOnly: boolean;
  onSelect(ids: string[]): void;
  onPatch(id: string, values: Partial<GameObject>): void;
  onMove(ids: string[], target: string, place: "before" | "after" | "inside"): void;
};
// Layers panel in Figma order: the top of the list is drawn on top. Only the
// rows in view are rendered, so pages with thousands of layers stay fast.
export function LayersPanel(props: Props) {
  const { objects, selected } = props,
    [closed, setClosed] = useState<Set<string>>(new Set()),
    [scroll, setScroll] = useState(0),
    [height, setHeight] = useState(480),
    [renaming, setRenaming] = useState<string | null>(null),
    [drop, setDrop] = useState<{ id: string; place: "before" | "after" | "inside" } | null>(null),
    dragging = useRef<string[]>([]),
    anchor = useRef<string | null>(null),
    list = useRef<HTMLDivElement>(null);
  const rows = useMemo(() => {
    const children = childrenOf(objects),
      out: { object: GameObject; depth: number; hasChildren: boolean }[] = [];
    const walk = (parent: string | null, depth: number) => {
      const kids = children.get(parent) || [];
      for (let i = kids.length - 1; i >= 0; i--) {
        const o = kids[i],
          inner = children.get(o.id)?.length || 0;
        out.push({ object: o, depth, hasChildren: inner > 0 });
        if (inner && !closed.has(o.id)) walk(o.id, depth + 1);
      }
    };
    walk(null, 0);
    return out;
  }, [objects, closed]);
  const first = Math.max(0, Math.floor(scroll / ROW) - 5),
    last = Math.min(rows.length, Math.ceil((scroll + height) / ROW) + 5);
  function click(e: React.MouseEvent, id: string) {
    if (e.shiftKey && anchor.current) {
      const a = rows.findIndex((r) => r.object.id === anchor.current),
        b = rows.findIndex((r) => r.object.id === id);
      if (a >= 0 && b >= 0) {
        const range = rows
          .slice(Math.min(a, b), Math.max(a, b) + 1)
          .map((r) => r.object.id);
        props.onSelect([...new Set([...selected, ...range])]);
        return;
      }
    }
    anchor.current = id;
    if (e.ctrlKey || e.metaKey)
      props.onSelect(
        selected.includes(id)
          ? selected.filter((s) => s !== id)
          : [...selected, id],
      );
    else props.onSelect([id]);
  }
  return (
    <aside className="layers-panel" aria-label="Слои">
      <div className="layers-head">
        <span className="eyebrow">СЛОИ</span>
        <span className="small-text muted">{objects.length}</span>
      </div>
      <div
        ref={(el) => {
          list.current = el;
          if (el && el.clientHeight && el.clientHeight !== height)
            setHeight(el.clientHeight);
        }}
        className="layers-list"
        role="tree"
        aria-multiselectable="true"
        onScroll={(e) => setScroll(e.currentTarget.scrollTop)}
      >
        {!rows.length && (
          <p className="small-text muted layers-empty">
            Добавьте объекты или перетащите картинки на холст.
          </p>
        )}
        <div style={{ height: rows.length * ROW, position: "relative" }}>
          {rows.slice(first, last).map(({ object: o, depth, hasChildren }, i) => {
            const Icon = icons[o.kind],
              container = o.kind === "group" || o.kind === "frame",
              target = drop?.id === o.id ? drop.place : null;
            return (
              <div
                key={o.id}
                role="treeitem"
                aria-selected={selected.includes(o.id)}
                aria-expanded={hasChildren ? !closed.has(o.id) : undefined}
                aria-level={depth + 1}
                data-layer={o.id}
                className={`layer-row ${selected.includes(o.id) ? "selected" : ""} ${o.visible ? "" : "is-hidden"} ${target ? `drop-${target}` : ""}`}
                style={{ top: (first + i) * ROW, paddingLeft: 6 + depth * 14 }}
                draggable={!props.readOnly && renaming !== o.id}
                onClick={(e) => click(e, o.id)}
                onDoubleClick={() => !props.readOnly && setRenaming(o.id)}
                onDragStart={(e) => {
                  dragging.current = selected.includes(o.id) ? selected : [o.id];
                  e.dataTransfer.effectAllowed = "move";
                  e.dataTransfer.setData("text/plain", o.name);
                }}
                onDragOver={(e) => {
                  if (!dragging.current.length) return;
                  e.preventDefault();
                  const r = e.currentTarget.getBoundingClientRect(),
                    y = (e.clientY - r.top) / r.height;
                  // The list is reversed: "before" in paint order is lower.
                  const place =
                    container && y > 0.25 && y < 0.75
                      ? "inside"
                      : y < 0.5
                        ? "after"
                        : "before";
                  if (drop?.id !== o.id || drop.place !== place)
                    setDrop({ id: o.id, place });
                }}
                onDragLeave={() => setDrop(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  if (drop && !dragging.current.includes(o.id))
                    props.onMove(dragging.current, o.id, drop.place);
                  setDrop(null);
                  dragging.current = [];
                }}
                onDragEnd={() => {
                  setDrop(null);
                  dragging.current = [];
                }}
              >
                <button
                  className="layer-toggle"
                  tabIndex={-1}
                  aria-label={closed.has(o.id) ? "Развернуть" : "Свернуть"}
                  style={{ visibility: hasChildren ? "visible" : "hidden" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const next = new Set(closed);
                    if (next.has(o.id)) next.delete(o.id);
                    else next.add(o.id);
                    setClosed(next);
                  }}
                >
                  {closed.has(o.id) ? (
                    <ChevronRight size={12} />
                  ) : (
                    <ChevronDown size={12} />
                  )}
                </button>
                <Icon size={13} className="layer-icon" />
                {renaming === o.id ? (
                  <input
                    className="layer-rename"
                    aria-label="Название слоя"
                    autoFocus
                    defaultValue={o.name}
                    maxLength={160}
                    onClick={(e) => e.stopPropagation()}
                    onBlur={(e) => {
                      const name = e.target.value.trim();
                      if (name && name !== o.name) props.onPatch(o.id, { name });
                      setRenaming(null);
                    }}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === "Enter") e.currentTarget.blur();
                      if (e.key === "Escape") setRenaming(null);
                    }}
                  />
                ) : (
                  <span className="layer-name">{o.name}</span>
                )}
                <button
                  className={`layer-flag ${o.locked ? "on" : ""}`}
                  aria-label={o.locked ? "Разблокировать" : "Заблокировать"}
                  disabled={props.readOnly}
                  onClick={(e) => {
                    e.stopPropagation();
                    props.onPatch(o.id, { locked: !o.locked });
                  }}
                >
                  {o.locked ? <Lock size={12} /> : <Unlock size={12} />}
                </button>
                <button
                  className={`layer-flag ${o.visible ? "" : "on"}`}
                  aria-label={o.visible ? "Скрыть при запуске" : "Показать при запуске"}
                  disabled={props.readOnly}
                  onClick={(e) => {
                    e.stopPropagation();
                    props.onPatch(o.id, { visible: !o.visible });
                  }}
                >
                  {o.visible ? <Eye size={12} /> : <EyeOff size={12} />}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </aside>
  );
}
